/**
 * Actions Configuration - Override Default Actions and Add Custom Actions
 *
 * This file shows how to override CE.SDK's default actions with your own
 * implementations, and how to register custom actions for the Advanced Video Editor.
 *
 * ## Actions API
 *
 * - `cesdk.actions.register(id, handler)` - Register or override an action
 * - `cesdk.actions.run(id, ...args)` - Execute an action (async, throws if not found)
 * - `cesdk.actions.get(id)` - Get action handler (returns undefined if not found)
 * - `cesdk.actions.list()` - List all registered action IDs
 *
 * ## Built-in Utility Functions
 *
 * CE.SDK provides utilities for common operations:
 *
 * ## Built-in Utility Functions
 *
 * CE.SDK provides utilities for common operations that you can use in your actions:
 *
 * - `cesdk.utils.export(options)` - Export current design to various formats
 *   - Options: mimeType, targetWidth, targetHeight, jpegQuality, pngCompressionLevel
 *   - Returns: { blobs: Blob[], options: ExportOptions }
 *
 * - `cesdk.utils.downloadFile(data, mimeType, filename?)` - Trigger browser file download
 *   - data: Blob, string, or ArrayBuffer
 *   - mimeType: MIME type (e.g., 'image/png', 'application/json')
 *   - filename: Optional filename (auto-generated if not provided)
 *
 * - `cesdk.utils.loadFile(options)` - Open browser file picker
 *   - Options: accept (file extensions), returnType ('text', 'arrayBuffer', 'objectURL')
 *   - Returns: Promise<string | ArrayBuffer | string> based on returnType
 *
 * @see https://img.ly/docs/cesdk/js/actions-6ch24x
 * @see https://img.ly/docs/cesdk/js/export-save-publish/export/overview-9ed3a8/
 */

import type CreativeEditorSDK from '@cesdk/cesdk-js';
import TemplatesApi from '../../../templates/Templates.api';

// Only music from the Epidemic / whitelisted catalog may be used so templates
// pass PEX brand safety checks.
const AUDIO_UPLOAD_NOT_ALLOWED_MESSAGE =
  'Audio uploads are not allowed. Please choose music from the Audio library.';

/**
 * Captures a still frame from a video file by loading it into an offscreen
 * <video> element and drawing the decoded frame to a canvas.
 */
function captureVideoFrame(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const videoUrl = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.src = videoUrl;

    let settled = false;
    const cleanup = () => URL.revokeObjectURL(videoUrl);
    const fail = (error: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    const timeoutId = setTimeout(
      () => fail(new Error('Timed out generating video thumbnail')),
      8000
    );

    video.addEventListener('error', () =>
      fail(new Error('Failed to load video for thumbnail generation'))
    );

    video.addEventListener('loadeddata', () => {
      // Seek slightly into the video to avoid an all-black first frame.
      video.currentTime = Math.min(0.5, (video.duration || 0) / 2);
    });

    video.addEventListener('seeked', () => {
      if (settled) return;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 320;
      canvas.height = video.videoHeight || 180;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        fail(new Error('Canvas 2D context unavailable'));
        return;
      }
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        clearTimeout(timeoutId);
        settled = true;
        cleanup();
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to encode video thumbnail'));
        }
      }, 'image/png');
    });
  });
}

/**
 * Generates a poster-frame thumbnail for an uploaded video file and uploads
 * it alongside the video so the Uploads panel has an image it can render.
 */
async function generateVideoThumbUri(file: File): Promise<string> {
  const frameBlob = await captureVideoFrame(file);
  const thumbFile = new File([frameBlob], `${file.name}.thumbnail.png`, {
    type: 'image/png'
  });
  const { url } = await TemplatesApi.uploadCesdkAsset(thumbFile);
  return url;
}

/**
 * Register actions and configure the navigation bar for Advanced Video Editor.
 *
 * Override default actions to integrate with your backend, or register
 * entirely custom actions for your application's needs.
 *
 * @param cesdk - The CreativeEditorSDK instance to configure
 *
 * @example Running actions programmatically
 * ```typescript
 * // Run built-in actions
 * await cesdk.actions.run('saveScene');
 * await cesdk.actions.run('exportVideo');
 * await cesdk.actions.run('exportDesign', { mimeType: 'video/mp4' });
 * await cesdk.actions.run('zoom.toPage', { page: 'current' });
 *
 * // Run custom actions
 * await cesdk.actions.run('myCustomAction', arg1, arg2);
 * ```
 *
 * @example Export video with custom settings
 * ```typescript
 * const { blobs } = await cesdk.utils.export({
 *   mimeType: 'video/mp4',
 *   fps: 60,
 *   quality: 0.95,
 *   targetWidth: 1920,
 *   targetHeight: 1080
 * });
 * ```
 */
export function setupActions(cesdk: CreativeEditorSDK): void {
  // ============================================================================
  // SCENE MANAGEMENT ACTIONS
  // Save and load scene files for editing workflows
  // ============================================================================

  // #region Save Scene Action
  // Save the current scene as a .scene JSON file
  // Users can reload this file later to continue editing
  cesdk.actions.register('saveScene', async () => {
    const scene = await cesdk.engine.scene.saveToString();
    await cesdk.utils.downloadFile(scene, 'text/plain;charset=UTF-8');
  });
  // #endregion

  // #region Import Scene Action
  // Load a previously saved scene file
  // Supports both .scene (JSON) and .zip (archive) formats
  cesdk.actions.register('importScene', async ({ format = 'scene' }) => {
    if (format === 'scene') {
      // Load from .scene JSON file
      const scene = await cesdk.utils.loadFile({
        accept: '.scene',
        returnType: 'text'
      });
      await cesdk.engine.scene.loadFromString(scene);
    } else {
      // Load from .zip archive file
      const blobURL = await cesdk.utils.loadFile({
        accept: '.zip',
        returnType: 'objectURL'
      });
      try {
        await cesdk.engine.scene.loadFromArchiveURL(blobURL);
      } finally {
        URL.revokeObjectURL(blobURL);
      }
    }

    // Zoom to fit the first page after loading
    await cesdk.actions.run('zoom.toPage', { page: 'first' });
  });
  // #endregion

  // #region Export Scene Action
  // Export the scene for storage or version control
  // Archive format includes all embedded assets
  cesdk.actions.register('exportScene', async ({ format = 'scene' }) => {
    await cesdk.utils.downloadFile(
      format === 'archive'
        ? await cesdk.engine.scene.saveToArchive()
        : await cesdk.engine.scene.saveToString(),
      format === 'archive' ? 'application/zip' : 'text/plain;charset=UTF-8'
    );
  });
  // #endregion

  // ============================================================================
  // VIDEO EXPORT ACTIONS
  // Export the final video in various formats
  // ============================================================================

  // #region Export Video Action
  // Export the timeline as an MP4 video file
  // Uses default export settings (30fps, 0.85 quality)
  cesdk.actions.register('exportVideo', async () => {
    const { blobs, options } = await cesdk.utils.export({
      mimeType: 'video/mp4'
    });
    await cesdk.utils.downloadFile(blobs[0], options.mimeType);
  });
  // #endregion

  // #region Export Design Action
  // Generic export action supporting multiple formats
  // Used by the UI for exporting with custom options
  cesdk.actions.register('exportDesign', async (exportOptions) => {
    const { blobs, options } = await cesdk.utils.export(exportOptions);
    await cesdk.utils.downloadFile(blobs[0], options.mimeType);
  });
  // #endregion

  // ============================================================================
  // ASSET UPLOAD ACTIONS
  // Handle user file uploads for videos and images
  // ============================================================================

  // #region Upload File Action
  cesdk.actions.register('uploadFile', async (file, onProgress) => {
    const kind = file.type.split('/')[0];
    if (kind === 'audio') {
      cesdk.ui.showNotification({
        type: 'error',
        message: AUDIO_UPLOAD_NOT_ALLOWED_MESSAGE
      });
      throw new Error(AUDIO_UPLOAD_NOT_ALLOWED_MESSAGE);
    }

    const { url } = await TemplatesApi.uploadCesdkAsset(file);

    // The raw file URL only works as a thumbnail for images. Video URLs aren't
    // renderable in an <img>, so the Uploads panel would show a blank tile
    // for them unless we supply a real image thumbnail instead.
    let thumbUri = url;
    if (kind === 'video') {
      thumbUri = await generateVideoThumbUri(file).catch((error) => {
        console.warn('Failed to generate video thumbnail:', error);
        return url;
      });
    }

    onProgress?.(1);
    return {
      id: url,
      meta: {
        uri: url,
        thumbUri,
        mimeType: file.type,
        filename: file.name,
        kind
      }
    };
  });
  // #endregion

  // ============================================================================
  // CUSTOM ACTIONS (COMMENTED OUT)
  // Example custom actions you can enable and modify
  // ============================================================================

  // #region Add Clip Action
  // Custom action to open asset library for adding video/image clips
  // cesdk.actions.register('addClip', async () => {
  //   cesdk.ui.openPanel('//ly.img.panel/assetLibrary', {
  //     payload: {
  //       entries: ['ly.img.image', 'ly.img.video'],
  //       applyAssetContext: {
  //         clipType: 'clip'
  //       }
  //     }
  //   });
  // });
  // #endregion

  // #region Share Action
  // Share video using the Web Share API or fallback to download
  // cesdk.actions.register('share', async () => {
  //   const { blobs } = await cesdk.utils.export({ mimeType: 'video/mp4' });
  //   const file = new File([blobs[0]], 'video.mp4', { type: 'video/mp4' });
  //
  //   if (navigator.share && navigator.canShare({ files: [file] })) {
  //     await navigator.share({
  //       files: [file],
  //       title: 'My Video',
  //       text: 'Check out my video!'
  //     });
  //   } else {
  //     await cesdk.utils.downloadFile(blobs[0], 'video/mp4');
  //   }
  // });
  // #endregion
}
